<template>
    <div class="tw-relative">
        <router-link :to="{ name: 'shared.messages' }" class="tw-flex tw-items-center tw-justify-start tw-w-[40px] tw-h-[40px] tw-text-primary-950 hover:tw-text-primary-600 tw-duration-200">
            <icon icon="solar:chat-round-call-linear" class="tw-text-2xl" />
            <span v-if="unreadCount > 0" class="tw-text-xs tw-text-white tw-font-medium tw-absolute -tw-top-0 tw-right-3 tw-bg-primary-500 tw-rounded-full tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center">
                {{ unreadCount > 99 ? '99+' : unreadCount }}
            </span>
        </router-link>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, onActivated } from "vue";
import { useAuthStore } from '@/stores';
import { useStateStore } from '@/stores/app/state';
import { useRoute } from 'vue-router';

const authStore = useAuthStore();
const stateStore = useStateStore();
const route = useRoute();
const messagesManager = ref(null);

// Get unread count from store
const unreadCount = computed(() => stateStore.unreadMessagesCount || 0);

// Initialize the messages manager
onMounted(async () => {
  if (authStore.user && authStore.user.id) {
    await initMessagesManager();
  }
});

// Re-ensure channel is active when component is activated (e.g., navigating back)
onActivated(async () => {
  if (messagesManager.value && authStore.user?.id) {
    await messagesManager.value.ensureChannelActive();
  }
});

// Watch for route changes to re-ensure channel (in case it was lost)
// This is important because ConversationList might leave the channel when unmounting
watch(() => route.path, async (newPath, oldPath) => {
  // If we navigated away from messages view, ensure channel is still active
  // Use a small delay to ensure ConversationList cleanup has completed
  if (oldPath && oldPath.includes('/messages') && !newPath.includes('/messages')) {
    if (messagesManager.value && authStore.user?.id) {
      // Small delay to let ConversationList cleanup complete
      setTimeout(async () => {
        await messagesManager.value.ensureChannelActive();
      }, 100);
    }
  }
  // Also check when navigating to any route (in case we're coming from messages)
  else if (oldPath && oldPath.includes('/messages')) {
    if (messagesManager.value && authStore.user?.id) {
      setTimeout(async () => {
        await messagesManager.value.ensureChannelActive();
      }, 100);
    }
  }
});

// Watch for user changes to re-initialize
watch(() => authStore.user?.id, async (newUserId, oldUserId) => {
  if (newUserId && newUserId !== oldUserId) {
    // User changed, re-initialize
    if (messagesManager.value) {
      messagesManager.value.cleanup();
    }
    await initMessagesManager();
  } else if (!newUserId) {
    // User logged out, cleanup
    if (messagesManager.value) {
      messagesManager.value.reset();
    }
  }
});

// Clean up listeners when component is unmounted
onUnmounted(() => {
  if (messagesManager.value) {
    messagesManager.value.removeAllEventListeners();
  }
});

// Initialize the messages manager
async function initMessagesManager() {
  try {
    // Import the messages manager dynamically to avoid SSR issues
    const { messagesManager: manager } = await import('@/services/messagesManager');
    messagesManager.value = manager;
    
    // Initialize with current user ID
    await messagesManager.value.init(authStore.user.id);
    
    // Add event listener for count updates
    messagesManager.value.addEventListener('onCountUpdated', handleCountUpdate);
  } catch (error) {
    console.error('Failed to initialize messages manager:', error);
  }
}

// Handle unread count update
function handleCountUpdate() {
  // Count is already updated in the store by the manager
}
</script>

<style scoped></style>